/**
 * Poll and refresh token hook.
 * This file exists to break circular dependencies between preauth-company.slice.ts and brightback-auth.slice.ts
 */

import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';
import { isUnauthorizedError } from '@bbkAdminRedux/rtkq/auth-status';
import { SignInType } from '@bbkAdminRedux/rtkq/auth-types';
import { selectAuthStatus } from '@bbkAdminRedux/rtkq/auth-utils';
import {
  brightbackAuthSlice,
  CONFIG_NOT_LOADED_ERROR,
  NOT_SIGNED_IN_ERROR,
  selectAuth,
  UNSUPPORTED_AUTH_SESSION_ERROR,
} from '@bbkAdminRedux/rtkq/brightback-auth.slice';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { getNowInSeconds } from '@bbkAdminUtils/utils';

// Refresh this many seconds before the session is due to expire.
const REFRESH_LEAD_SECONDS = 30;
// Floor for the poll interval once the session is at/near expiry.
const MIN_POLL_SECONDS = 10;
// Ceiling for exponential backoff after transient refresh failures.
const MAX_BACKOFF_SECONDS = 300;

const getErrorMessage = (err: unknown) =>
  err instanceof Error
    ? err.message
    : typeof err === 'object' &&
        err !== null &&
        'message' in err &&
        typeof err.message === 'string'
      ? err.message
      : undefined;

const getErrorStatus = (err: unknown): number | undefined => {
  if (!err || typeof err !== 'object') {
    return undefined;
  }
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
};

/**
 * A failed refresh is terminal (the session is genuinely unusable, so sign out)
 * only for: an unsupported/legacy stored session, a missing session, or a
 * server rejection (401/403). Everything else — a rate-limited 429, a 5xx, or a
 * network/timeout error — is transient: a momentary blip during the periodic
 * refresh must not log the user out, so it is retried with backoff. A truly
 * dead session still surfaces as a 401 on the next real request and is handled
 * by the unauthorized-redirect middleware.
 */
const isTerminalRefreshError = (err: unknown): boolean => {
  const message = getErrorMessage(err);
  if (
    message === UNSUPPORTED_AUTH_SESSION_ERROR ||
    message === NOT_SIGNED_IN_ERROR
  ) {
    return true;
  }
  if (isUnauthorizedError(err)) {
    return true;
  }
  return getErrorStatus(err) === 403;
};

export const usePollAndRefreshToken = () => {
  const [doRefreshTokens$] =
    brightbackAuthSlice.endpoints.doRefreshTokens.useMutation();
  const [signOut$] = brightbackAuthSlice.endpoints.signOut.useMutation();
  const [upgradeSession$] =
    brightbackAuthSlice.endpoints.upgradeSession.useMutation();

  // Mirror the reactive auth status + session metadata into refs so the
  // self-scheduling timer (memoized with []) reads the latest values at fire
  // time without an imperative store read.
  const authStatus = useSelector(selectAuthStatus);
  const authStatusRef = useRef(authStatus);
  authStatusRef.current = authStatus;

  const auth = useSelector(selectAuth);
  const authRef = useRef(auth);
  authRef.current = auth;

  // Consecutive transient refresh failures, driving exponential backoff so a
  // momentary 429/5xx does not immediately log the user out.
  const transientFailuresRef = useRef(0);

  // Single-flight guard so overlapping ticks within a tab do not fire
  // concurrent refreshes against the backend.
  const refreshingRef = useRef(false);

  // Single-flight guard for the upgrade, which both the bootstrap effect and
  // the poll tick can trigger.
  const bootstrappingRef = useRef(false);

  /**
   * Migrates any JS-created cookie to the HttpOnly attributes and hydrates the
   * `{ type, exp }` session metadata. Idempotent, and never rejects: the caller
   * decides when to try again.
   */
  const bootstrapSession = useCallback(async (): Promise<void> => {
    if (bootstrappingRef.current) {
      return;
    }
    bootstrappingRef.current = true;
    try {
      await upgradeSession$().unwrap();
    } catch {
      // Upgrade is best-effort; a failure here does not end the session. The
      // poll tick retries for as long as the metadata is missing, and a
      // genuinely dead session surfaces as a 401 elsewhere.
    } finally {
      bootstrappingRef.current = false;
    }
  }, [upgradeSession$]);

  // The session credential now lives in a backend HttpOnly cookie. Time
  // remaining is derived from the non-secret `exp` metadata.
  const getTimeRemaining = useCallback(
    () => (authRef.current?.exp ?? 0) - getNowInSeconds(),
    []
  );

  const normalDelay = useCallback(() => {
    const remaining = getTimeRemaining();
    return remaining > 60 ? remaining - REFRESH_LEAD_SECONDS : MIN_POLL_SECONDS;
  }, [getTimeRemaining]);

  // Runs one refresh attempt when appropriate and returns how many seconds to
  // wait before the next tick, or `null` to stop polling (dev sessions).
  const runRefreshCycle = useCallback(async (): Promise<number | null> => {
    if (authStatusRef.current !== 'authenticated') {
      return normalDelay();
    }
    // The upgrade that hydrates `{ type, exp }` has not landed yet — first
    // load, or it failed and left us with no metadata. Refreshing blind would
    // hit a session whose type we do not know, and the backend terminally
    // rejects the refresh of a dev session, signing the user out. Retry the
    // bootstrap instead and reconsider on the next tick.
    if (!authRef.current) {
      await bootstrapSession();
      return MIN_POLL_SECONDS;
    }
    // Dev sessions do not support (and do not need) backend refresh.
    if (authRef.current.type === SignInType.DEV) {
      return null;
    }
    // Single-flight: a refresh is already running, come back on the next tick.
    if (refreshingRef.current) {
      return MIN_POLL_SECONDS;
    }

    refreshingRef.current = true;
    try {
      await doRefreshTokens$().unwrap();
      transientFailuresRef.current = 0;
      return normalDelay();
    } catch (err) {
      const message = getErrorMessage(err);
      // The client config never loaded (e.g. a transient /client_config failure
      // on boot). Nothing actionable here: retry on the next tick.
      if (message === CONFIG_NOT_LOADED_ERROR) {
        return MIN_POLL_SECONDS;
      }

      if (isTerminalRefreshError(err)) {
        transientFailuresRef.current = 0;
        if (message !== UNSUPPORTED_AUTH_SESSION_ERROR) {
          GlobalLogger.error(err);
        }
        await signOut$({ redirect: LogInPaths.signIn })
          .unwrap()
          .catch(() => {
            // signOut navigates away on success; swallow its own failure so a
            // rejected sign-out does not surface as an unhandled rejection.
          });
        return MAX_BACKOFF_SECONDS;
      }

      // Transient failure: keep the session and retry with exponential backoff.
      transientFailuresRef.current += 1;
      GlobalLogger.error(err);
      return Math.min(
        MIN_POLL_SECONDS * 2 ** (transientFailuresRef.current - 1),
        MAX_BACKOFF_SECONDS
      );
    } finally {
      refreshingRef.current = false;
    }
  }, [bootstrapSession, doRefreshTokens$, normalDelay, signOut$]);

  const scheduleNextRefresh = useCallback(
    (delaySeconds: number) => {
      if (delaySeconds > MIN_POLL_SECONDS) {
        console.log(
          `usePollAndRefreshToken, set up timer to refresh token in ${delaySeconds} seconds`
        );
      }
      setTimeout(async () => {
        const nextDelaySeconds = await runRefreshCycle();
        if (nextDelaySeconds !== null) {
          scheduleNextRefresh(nextDelaySeconds);
        }
      }, delaySeconds * 1000);
    },
    [runRefreshCycle]
  );

  // Bootstrap: once the current-user probe confirms an authenticated session,
  // upgrade it. A failure is picked up by the poll tick above, which keeps
  // retrying while the metadata is missing.
  const bootstrappedRef = useRef(false);
  useEffect(() => {
    if (authStatus === 'authenticated' && !bootstrappedRef.current) {
      bootstrappedRef.current = true;
      void bootstrapSession();
    }
  }, [authStatus, bootstrapSession]);

  useEffect(() => {
    scheduleNextRefresh(MIN_POLL_SECONDS);
  }, [scheduleNextRefresh]);
};
